import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { Box, Card, Divider, Group, SkeletonRect, Stack, Text } from '@ekhodealer/ekho-common/rev';

import { formatNumberWithCommas } from '@common/lib/currency';

import {
	isGeneratingPaymentReconciliationReportAtom,
	paymentReconciliationReportDataAtom,
} from '../../atoms/paymentReconciliationAtoms';

import './styles.css';

export default function ReportsPaymentReconciliationSummary() {
	const reportData = useAtomValue(paymentReconciliationReportDataAtom);
	const isPending = useAtomValue(isGeneratingPaymentReconciliationReportAtom);

	const summary = useMemo(() => {
		if (!reportData || reportData.length === 0) {
			return {
				paymentsTotal: {
					brex: 0,
					stripe: 0,
				},
				paymentsCount: {
					brex: 0,
					stripe: 0,
				},
			};
		}

		const totals = reportData.reduce(
			(acc, item) => {
				const transferAmount = parseFloat(item.transferAmount || '0');
				const platform = (item.transferPlatform || '').toLowerCase();

				if (platform.includes('brex')) {
					acc.paymentsTotal.brex += transferAmount;
					acc.paymentsCount.brex += 1;
				} else if (platform.includes('stripe')) {
					acc.paymentsTotal.stripe += transferAmount;
					acc.paymentsCount.stripe += 1;
				} else {
					// Count other platforms but categorize as brex for now
					acc.paymentsTotal.brex += transferAmount;
					acc.paymentsCount.brex += 1;
				}

				return acc;
			},
			{
				paymentsTotal: { brex: 0, stripe: 0 },
				paymentsCount: { brex: 0, stripe: 0 },
			},
		);

		return totals;
	}, [reportData]);

	// Show skeleton when loading
	if (isPending) {
		return (
			<div className='summary-container'>
				<div className='summary-item'>
					<div className='summary-card'>
						<div className='metrics-card-content'>
							<div className='main-metric-group'>
								<SkeletonRect width={120} height={27} rounding='8' />
								<SkeletonRect width={100} height={17} rounding='8' />
							</div>
							<div className='sub-metrics-container'>
								<div className='sub-metric-item'>
									<SkeletonRect width={60} height={17} rounding='8' />
									<SkeletonRect width={80} height={19} rounding='8' />
								</div>
								<div className='sub-metric-item'>
									<SkeletonRect width={60} height={17} rounding='8' />
									<SkeletonRect width={80} height={19} rounding='8' />
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className='summary-item'>
					<div className='summary-card'>
						<div className='metrics-card-content'>
							<div className='main-metric-group'>
								<SkeletonRect width={60} height={27} rounding='8' />
								<SkeletonRect width={120} height={17} rounding='8' />
							</div>
							<div className='sub-metrics-container'>
								<div className='sub-metric-item'>
									<SkeletonRect width={60} height={17} rounding='8' />
									<SkeletonRect width={40} height={19} rounding='8' />
								</div>
								<div className='sub-metric-item'>
									<SkeletonRect width={60} height={17} rounding='8' />
									<SkeletonRect width={40} height={19} rounding='8' />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Don't show summary if no report has been generated yet, or if report is empty
	if (reportData === null || reportData.length === 0) {
		return null;
	}

	const totalPayments = summary.paymentsTotal.brex + summary.paymentsTotal.stripe;
	const totalCount = summary.paymentsCount.brex + summary.paymentsCount.stripe;

	return (
		<div className='summary-container'>
			<Card className='p-0'>
				<Box padding={20} className='w-full'>
					<Stack itemsSpacing={20} itemsAlignX='stretch'>
						<Stack itemsSpacing={4} itemsAlignX='stretch'>
							<Text size='title100' weight='bold' align='left'>
								${formatNumberWithCommas(totalPayments.toFixed(2))}
							</Text>
							<Text size='footnote' weight='semibold' color='secondary' align='left'>
								Payments total
							</Text>
						</Stack>
						<Group itemsSpacing={8} noWrap itemsAlignX='stretch'>
							<Stack itemsSpacing={0}>
								<Group padding={8}>
									<Group.Item>
										<Text size='footnote' weight='semibold' color='secondary'>
											Brex
										</Text>
									</Group.Item>
									<Group.Item>
										<Text size='bodySmall' weight='semibold'>
											$
											{formatNumberWithCommas(
												summary.paymentsTotal.brex.toFixed(2),
											)}
										</Text>
									</Group.Item>
								</Group>
								<Divider />
							</Stack>
							<Stack itemsSpacing={0}>
								<Group padding={8}>
									<Group.Item>
										<Text size='footnote' weight='semibold' color='secondary'>
											Stripe
										</Text>
									</Group.Item>
									<Group.Item>
										<Text size='bodySmall' weight='semibold'>
											$
											{formatNumberWithCommas(
												summary.paymentsTotal.stripe.toFixed(2),
											)}
										</Text>
									</Group.Item>
								</Group>
								<Divider />
							</Stack>
						</Group>
					</Stack>
				</Box>
			</Card>
			<Card className='p-0'>
				<Box padding={20} className='w-full'>
					<Stack itemsSpacing={20} itemsAlignX='stretch'>
						<Stack itemsSpacing={4} itemsAlignX='stretch'>
							<Text size='title100' weight='bold' align='left'>
								{totalCount}
							</Text>
							<Text size='footnote' weight='semibold' color='secondary' align='left'>
								No. of payments
							</Text>
						</Stack>
						<Group itemsSpacing={8} noWrap itemsAlignX='stretch'>
							<Stack itemsSpacing={0}>
								<Group padding={8}>
									<Group.Item>
										<Text size='footnote' weight='semibold' color='secondary'>
											Brex
										</Text>
									</Group.Item>
									<Group.Item>
										<Text size='bodySmall' weight='semibold'>
											{summary.paymentsCount.brex}
										</Text>
									</Group.Item>
								</Group>
								<Divider />
							</Stack>
							<Stack itemsSpacing={0}>
								<Group padding={8}>
									<Group.Item>
										<Text size='footnote' weight='semibold' color='secondary'>
											Stripe
										</Text>
									</Group.Item>
									<Group.Item>
										<Text size='bodySmall' weight='semibold'>
											{summary.paymentsCount.stripe}
										</Text>
									</Group.Item>
								</Group>
								<Divider />
							</Stack>
						</Group>
					</Stack>
				</Box>
			</Card>
		</div>
	);
}
